<script lang="ts">
import { defineComponent } from 'vue';
import YieldPromiseStatusApi from '@/data/yields/YieldPromiseStatusApi.ts';
import Company from '@/domain/model/business/Company';
import YieldPromiseStatus from '@/domain/model/business/YieldPromiseStatus.ts';
import Energy from '@/domain/model/unit/Energy.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { formatAsDate } from '@/infrastructure/support/utils/DateTimeUtils.ts';

export default defineComponent({
  name: 'LongTermGapFillModal',
  emits: ['fetch-required', 'update:visible'],
  props: {
    visible: { type: Boolean, required: true },
    company: { type: Company, required: true },
    period: { type: YieldPromiseStatus, required: true },
  },
  data() {
    return {
      yieldFilledInKwh: null,
      comment: null,
      loading: false,
      success: null,
      error: null,
    };
  },
  methods: {
    submit() {
      if (!this.yieldFilledInKwh) {
        return;
      }

      this.loading = true;
      this.success = null;
      this.error = null;

      this.createGapFill()
        .then(() => {
          this.$emit('fetch-required');
          this.success = true;
          this.yieldFilledInKwh = null;
          this.comment = null;
        })
        .catch(() => (this.error = true))
        .finally(() => (this.loading = false));
    },
    deleteGapFill(gapFillId: number) {
      this.$confirm.require({
        header: this.$t('global.confirm_message'),
        message: this.$t('features.long_term.gap_fills.delete_confirm'),
        accept: () => {
          new YieldPromiseStatusApi()
            .setMinimumDurationMsForCalls(1500)
            .deleteGapFill(
              this.company.getId(),
              this.period.getPlantId(),
              this.period.getId(),
              gapFillId,
            )
            .then(() => this.$emit('fetch-required'));
        },
      });
    },
    asLocalizedDate(dateString: string): string {
      return formatAsDate(new Date(dateString));
    },
    createGapFill(): Promise<any> {
      const api = new YieldPromiseStatusApi();
      return api
        .setMinimumDurationMsForCalls(1000)
        .createGapFill(
          this.company.getId(),
          this.period.getPlantId(),
          this.period.getId(),
          {
            user_id: useSessionStore().user.id,
            yield_missing_in_wh: this.period
              .getMissingEnergy()
              .asWh()
              .toFixed(0),
            yield_filled_in_wh: this.yieldFilledInKwh * 1000,
            comment: this.comment,
          },
        );
    },
  },
  computed: {
    Energy() {
      // Needed for import
      return Energy;
    },
    totalYieldFilled(): Energy {
      const totalYieldFilled = this.period
        .getActiveGapFills()
        .reduce((sum, gap) => sum + gap.yield_filled_in_wh, 0);

      return Energy.ofWh(totalYieldFilled);
    },
  },
});
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', false)"
    :header="$t('features.long_term.gap_fills.modal_title')"
    modal
  >
    <div style="max-width: 40rem">
      <div class="flex flex-col w-2/3">
        <span class="mb-2">
          <i18n-t keypath="features.long_term.gap_fills.dialog_period_yield">
            <template #yield_actual>
              <strong> {{ period.getDeliveredTillNow() }} </strong>
            </template>
            <template #yield_filled>
              <strong> {{ totalYieldFilled.toString() }} </strong>
            </template>
          </i18n-t>

          {{}}

          <i18n-t
            v-if="period.getMissingEnergy().gt(0)"
            keypath="features.long_term.gap_fills.dialog_period_missing"
          >
            <template #yield_missing>
              <strong> {{ period.getMissingEnergy() }} </strong>
            </template>
          </i18n-t>

          <i18n-t
            v-else
            keypath="features.long_term.gap_fills.dialog_period_ahead"
          >
            <template #yield_ahead>
              <strong> {{ period.getMissingEnergy().times(-1) }} </strong>
            </template>
          </i18n-t>
        </span>

        <Message v-if="period.isFinished()" severity="warn" :closable="false">
          {{ $t('features.long_term.gap_fills.period_ended') }}
        </Message>

        <InputGroup class="mt-1">
          <InputNumber
            v-model="yieldFilledInKwh"
            :placeholder="$t('features.long_term.gap_fills.gap_fill', 1)"
            :disabled="loading"
            autofocus
          />

          <InputGroupAddon>kWh</InputGroupAddon>
        </InputGroup>

        <InputText
          v-model="comment"
          :placeholder="$t('features.long_term.gap_fills.reason')"
          :disabled="loading"
          class="my-1"
        />

        <Button
          @click="submit"
          outlined
          :label="$t('global.save')"
          :loading="loading"
          :disabled="loading"
        />

        <Message v-if="success" severity="success" :closable="false">
          {{ $t('features.long_term.gap_fills.create_success') }}
        </Message>

        <Message v-if="error" severity="error" :closable="false">
          {{ $t('features.long_term.gap_fills.create_error') }}
        </Message>
      </div>

      <div v-if="period.getActiveGapFills().length !== 0">
        <Divider />
        <span class="my-2 text-muted">
          {{ $t('features.long_term.gap_fills.existing_title') }}
        </span>
        <ul>
          <li v-for="gapFill in period.getActiveGapFills()" :key="gapFill.id">
            <i18n-t keypath="features.long_term.gap_fills.existing_item">
              <template #created_at>
                <strong> {{ asLocalizedDate(gapFill.created_at) }} </strong>
              </template>

              <template #yield>
                <strong>
                  {{ Energy.ofWh(gapFill.yield_filled_in_wh).toString() }}
                </strong>
              </template>

              <template #reason>
                <br />
                <i>
                  {{
                    gapFill.comment ??
                    $t('features.long_term.gap_fills.reason_none')
                  }}
                </i>
              </template>
            </i18n-t>

            <span
              class="ml-2 hover:underline cursor-pointer text-slate-500"
              @click="deleteGapFill(gapFill.id)"
            >
              {{ $t('global.click_to_delete') }}
            </span>
          </li>
        </ul>
      </div>

      <ConfirmDialog />
    </div>

    <template #footer>
      <Button @click="$emit('update:visible')">
        {{ $t('global.close') }}
      </Button>
    </template>
  </Dialog>
</template>

<style scoped>
ul li {
  margin-left: 2rem;
  list-style-type: circle;
}
</style>
